import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";

/**
 * Escucha mensajes postMessage del service worker (sw-push.js) cuando
 * el usuario hace click en una push notification. El SW manda
 * `{ type: 'iftmx:navigate', url: '<destino>' }` y aqui usamos
 * react-router useNavigate para hacer la transicion limpia.
 *
 * Esto reemplaza el client.navigate() del SW que era flaky en iOS
 * Safari PWA (a veces enfocaba la ventana pero no cambiaba ruta).
 * Vive DENTRO de BrowserRouter para tener acceso a useNavigate.
 */
function SwNavigationListener() {
  const navigate = useNavigate();
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (e: MessageEvent) => {
      const data = e.data as any;
      if (data?.type !== 'iftmx:navigate' || !data?.url) return;
      try {
        const url = new URL(data.url, window.location.origin);
        // Solo navegar si es del mismo origen (defensa contra XSS via
        // mensajes externos, aunque solo nuestro SW envia este tipo).
        if (url.origin !== window.location.origin) return;
        navigate(url.pathname + url.search + url.hash);
      } catch { /* URL invalida — ignorar */ }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);
  return null;
}
// Eager-loaded routes — these are the most-visited entry points and
// blocking on a chunk fetch would feel worse than a slightly larger
// initial bundle (the auth flow, the client home, the welcome page).
import Index from "./pages/Index";
import Auth from "./pages/Auth";
import WelcomePage from "./pages/WelcomePage";

// Lazy routes — coach / admin / detail pages that the average client
// never visits. Splitting these out shrinks the first-paint bundle
// significantly, which is what was making the loader spin for 25+
// seconds on slow cellular.
const Onboarding = lazy(() => import("./pages/Onboarding"));
const NotFound = lazy(() => import("./pages/NotFound"));
const TermsAndPrivacy = lazy(() => import("./pages/TermsAndPrivacy"));
const CoachDashboard = lazy(() => import("./pages/CoachDashboard").then(m => ({ default: m.CoachDashboard })));
const CatalogPage = lazy(() => import("./pages/CatalogPage").then(m => ({ default: m.CatalogPage })));
const CoachPaymentsPage = lazy(() => import("./pages/CoachPaymentsPage"));
const CoachAvailabilityPage = lazy(() => import("./pages/CoachAvailabilityPage"));
const AdminSupportPage = lazy(() => import("./pages/AdminSupportPage"));
const SupportPage = lazy(() => import("./pages/SupportPage"));
const ViewClientPage = lazy(() => import("./pages/ViewClientPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminAnalyticsPage = lazy(() => import("./pages/AdminAnalyticsPage"));
const CoachSignup = lazy(() => import("./pages/CoachSignup"));
const CoachOnboarding = lazy(() => import("./pages/CoachOnboarding"));
const CoachAnalyticsPage = lazy(() => import("./pages/CoachAnalyticsPage"));
const CoachInvitePage = lazy(() => import("./pages/CoachInvitePage"));
const InviteLandingPage = lazy(() => import("./pages/InviteLandingPage"));
const TutorialPublicPage = lazy(() => import("./pages/TutorialPublicPage"));
const AdminCoachesPage = lazy(() => import("./pages/AdminCoachesPage"));
const AcercaPage = lazy(() => import("./pages/AcercaPage"));

import { ProtectedRoute } from "./components/ProtectedRoute";
import { CoachRoute } from "./components/coach/CoachRoute";
import { AdminRoute } from "./components/admin/AdminRoute";
import { SupportRoute } from "./components/support/SupportRoute";
import { ConnectionIndicator } from "./components/fitness/ConnectionIndicator";
import { OfflineBanner } from "./components/OfflineBanner";
import { CookieBanner } from "./components/CookieBanner";
import { GlobalErrorBoundary } from "./components/GlobalErrorBoundary";
import { PwaUpdateBanner } from "./components/PwaUpdateBanner";
import { TicketCreatedListener } from "./components/support/TicketCreatedListener";
import { RestTimerProvider } from "./contexts/RestTimerContext";
import { RestTimer } from "./components/fitness/RestTimer";
import { RestTimerPill } from "./components/fitness/RestTimerPill";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useAuth } from "./hooks/useAuth";
import { useStandaloneTracker } from "./hooks/useStandaloneTracker";
import { useLastSeenTracker } from "./hooks/useLastSeenTracker";
import { ThemeProvider } from "./contexts/ThemeContext";
import { SistemicOutro } from "./components/sistemic/SistemicOutro";

/** Reporta si el usuario abrió la app en modo standalone (PWA instalada).
 *  Vive a nivel App para que dispare sin importar qué vista esté activa
 *  (cliente, coach, admin). */
function StandaloneTrackerMount() {
  const { user } = useAuth();
  useStandaloneTracker(user?.id);
  // Genérico: marca last_seen_at en CUALQUIER apertura (browser o PWA).
  // Throttle 1h client-side; el RPC es no-op si no hay user_id.
  useLastSeenTracker(user?.id);
  return null;
}

/** Tiny full-screen spinner used while a lazy route's chunk loads. */
function RouteFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="w-8 h-8 border-4 border-accent/30 border-t-accent rounded-full animate-spin" />
    </div>
  );
}

const queryClient = new QueryClient();

function AppContent() {
  const { online, syncing, pendingCount, deadCount } = useOnlineStatus();
  return (
    <>
      <Toaster />
      <Sonner />
      <PwaUpdateBanner />
      <TicketCreatedListener />
      <StandaloneTrackerMount />
      {/* Cronómetro de descanso global — el modal y el pill flotante
       *  viven aquí porque deben persistir entre rutas. */}
      <RestTimer />
      <RestTimerPill />
      <ConnectionIndicator online={online} syncing={syncing} pendingCount={pendingCount} deadCount={deadCount} />
      <OfflineBanner />
      <CookieBanner />
      <BrowserRouter>
        <SwNavigationListener />
        <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/" element={<ProtectedRoute><Index /></ProtectedRoute>} />
          <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          {/* Signup público de coach — no requiere autenticación.
              El RPC claim_coach_role asigna el rol después del signup. */}
          <Route path="/coach/signup" element={<CoachSignup />} />

          {/* Landing pública de invitación — el cliente llega via
              link generado por su coach. Maneja signup/login + accept. */}
          <Route path="/invite/:token" element={<InviteLandingPage />} />

          {/* Tutorial público — sin login, para compartir por WhatsApp
              a prospectos que aún no tienen cuenta. */}
          <Route path="/ver/:tutorialId" element={<TutorialPublicPage />} />

          {/* Página del coach para generar y compartir invitaciones. */}
          <Route path="/coach/invitar" element={
            <ProtectedRoute>
              <CoachRoute>
                <CoachInvitePage />
              </CoachRoute>
            </ProtectedRoute>
          } />

          {/* Onboarding del coach — requiere estar autenticado pero
              no requiere rol coach todavía (recién signupado). Si ya
              terminó onboarding, redirige al dashboard solo. */}
          <Route path="/coach/onboarding" element={
            <ProtectedRoute>
              <CoachOnboarding />
            </ProtectedRoute>
          } />

          {/* Misma pantalla en modo edición (no auto-redirige). */}
          <Route path="/coach/perfil" element={
            <ProtectedRoute>
              <CoachRoute>
                <CoachOnboarding />
              </CoachRoute>
            </ProtectedRoute>
          } />

          <Route path="/coach" element={
            <ProtectedRoute>
              <CoachRoute>
                <CoachDashboard />
              </CoachRoute>
            </ProtectedRoute>
          } />
          <Route path="/coach/catalogo" element={
            <ProtectedRoute>
              <CoachRoute>
                <CatalogPage />
              </CoachRoute>
            </ProtectedRoute>
          } />
          <Route path="/coach/pagos" element={
            <ProtectedRoute>
              <CoachRoute>
                <CoachPaymentsPage />
              </CoachRoute>
            </ProtectedRoute>
          } />
          <Route path="/coach/analytics" element={
            <ProtectedRoute>
              <CoachRoute>
                <CoachAnalyticsPage />
              </CoachRoute>
            </ProtectedRoute>
          } />
          <Route path="/coach/disponibilidad" element={
            <ProtectedRoute>
              <CoachRoute>
                <CoachAvailabilityPage />
              </CoachRoute>
            </ProtectedRoute>
          } />
          <Route path="/view-client/:clientId" element={
            <ProtectedRoute>
              <CoachRoute>
                <ViewClientPage />
              </CoachRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminDashboard />
              </AdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/analytics" element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminAnalyticsPage />
              </AdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/coaches" element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminCoachesPage />
              </AdminRoute>
            </ProtectedRoute>
          } />
          <Route path="/admin/soporte" element={
            <ProtectedRoute>
              <SupportRoute>
                <AdminSupportPage />
              </SupportRoute>
            </ProtectedRoute>
          } />
          <Route path="/soporte" element={
            <ProtectedRoute>
              <SupportPage />
            </ProtectedRoute>
          } />
          <Route path="/auth/callback" element={<Auth />} />
          <Route path="/welcome" element={<WelcomePage />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/terminos" element={<TermsAndPrivacy initialTab="terms" />} />
          <Route path="/privacidad" element={<TermsAndPrivacy initialTab="privacy" />} />
          {/* /acerca — landing pequeña de Sistemic.
              Pública (sin login required) para que coaches prospectos
              que reciban link puedan entrar sin barrera. */}
          <Route path="/acerca" element={<AcercaPage />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        {/* Outro de Sistemic — sección final con identidad visual
            completa (gradient azul, marquee de servicios, NodeMark
            con glow, wordmark animado, CTA). Aparece bajo cualquier
            ruta excepto /acerca (donde ya hay info equivalente). */}
        <SistemicOutro />
      </BrowserRouter>
    </>
  );
}

const App = () => (
  <GlobalErrorBoundary>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <RestTimerProvider>
          <TooltipProvider>
            <AppContent />
          </TooltipProvider>
        </RestTimerProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </GlobalErrorBoundary>
);

export default App;
